import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { X, Send, Loader2, Bot, User, Plus, History, Trash2, ChevronLeft } from "lucide-react";

const EdgeAIAssistant = () => {
  const savedSession = sessionStorage.getItem("ai_session_id");
  const [open, setOpen] = useState(() => {
    const saved = sessionStorage.getItem("ai_assistant_open");
    return saved !== null ? saved === "true" : true;
  });
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(() => savedSession || null);
  const [messages, setMessages] = useState(
    savedSession ? [] : [{ role: "assistant", content: "Hi! I'm **Edge AI Assistant**. I can help you with questions about your orders, deliveries, appointments, wallet, and more. What would you like to know?" }]
  );
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [restoringSession, setRestoringSession] = useState(!!savedSession);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Persist open/close and session ID to sessionStorage
  useEffect(() => { sessionStorage.setItem("ai_assistant_open", open); }, [open]);
  useEffect(() => {
    if (currentSessionId) sessionStorage.setItem("ai_session_id", currentSessionId);
    else sessionStorage.removeItem("ai_session_id");
  }, [currentSessionId]);

  // Load last session on mount
  useEffect(() => {
    if (savedSession) {
      loadSession(savedSession).finally(() => setRestoringSession(false));
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await axios.get(`${API}/ai-assistant/sessions`);
      setSessions(res.data);
    } catch (e) {
      console.error("Failed to fetch sessions", e);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/ai-assistant/sessions/${sessionId}/messages`);
      setMessages(res.data.messages.map(m => ({ role: m.role, content: m.content })));
      setCurrentSessionId(sessionId);
      setShowHistory(false);
    } catch (e) {
      console.error("Failed to load session", e);
    }
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/ai-assistant/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (currentSessionId === sessionId) startNewChat();
    } catch (err) {
      console.error("Failed to delete session", err);
    }
  };

  const startNewChat = () => {
    setCurrentSessionId(null);
    setMessages([
      { role: "assistant", content: "Hi! I'm **Edge AI Assistant**. What would you like to know?" }
    ]);
    setShowHistory(false);
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: "user", content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      const apiMessages = newMessages
        .filter((m, i) => m.role === "user" || (m.role === "assistant" && i > 0))
        .map(m => ({ role: m.role, content: m.content }));

      const response = await axios.post(`${API}/ai-assistant/chat`, {
        session_id: currentSessionId,
        messages: apiMessages
      });

      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
      if (response.data.session_id && !currentSessionId) {
        setCurrentSessionId(response.data.session_id);
      }
    } catch (error) {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I encountered an error. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderContent = (text) => {
    let html = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/`([^`]+)`/g, '<code class="bg-slate-200 px-1 py-0.5 rounded text-xs">$1</code>');
    html = html.replace(/\n/g, '<br/>');
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const openHistory = () => {
    setShowHistory(true);
    fetchSessions();
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-110"
          style={{ backgroundColor: '#00A699' }}
          data-testid="ai-assistant-btn"
        >
          <Bot className="w-6 h-6 text-white" />
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 z-50 w-auto sm:w-[400px] h-[70vh] sm:h-[560px] max-h-[600px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="ai-assistant-window">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100" style={{ backgroundColor: '#00A699' }}>
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-white" />
              <div>
                <span className="text-sm font-semibold text-white">Edge AI Assistant</span>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-300 rounded-full"></span>
                  <span className="text-xs text-white/80">Online</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={startNewChat} className="p-1.5 text-white/80 hover:text-white rounded transition-colors" title="New Chat">
                <Plus className="w-4 h-4" />
              </button>
              <button onClick={openHistory} className="p-1.5 text-white/80 hover:text-white rounded transition-colors" title="Chat History">
                <History className="w-4 h-4" />
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5 text-white/80 hover:text-white rounded transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* History Panel */}
          {showHistory ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100">
                <button onClick={() => setShowHistory(false)} className="p-1 text-slate-500 hover:text-slate-700 rounded">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium text-slate-700">Chat History</span>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loadingSessions ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                ) : sessions.length === 0 ? (
                  <div className="text-center py-8 text-sm text-slate-400">No previous conversations</div>
                ) : (
                  sessions.map(session => (
                    <div
                      key={session.id}
                      onClick={() => loadSession(session.id)}
                      className={`flex items-center justify-between px-4 py-3 border-b border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors ${
                        currentSessionId === session.id ? 'bg-teal-50' : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0 mr-2">
                        <p className="text-sm text-slate-700 truncate">{session.title}</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {new Date(session.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <button
                        onClick={(e) => deleteSession(session.id, e)}
                        className="p-1 text-slate-300 hover:text-red-500 rounded transition-colors flex-shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {restoringSession ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : messages.map((msg, i) => (
                  <div key={i} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    {msg.role === "assistant" && (
                      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: '#00A699' }}>
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                      msg.role === "user"
                        ? "bg-teal-600 text-white rounded-br-md"
                        : "bg-slate-100 text-slate-800 rounded-bl-md"
                    }`}>
                      {renderContent(msg.content)}
                    </div>
                    {msg.role === "user" && (
                      <div className="w-7 h-7 rounded-full bg-slate-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <User className="w-4 h-4 text-slate-600" />
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-2 justify-start">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#00A699' }}>
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-slate-100 px-4 py-3 rounded-xl rounded-bl-md">
                      <div className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="px-3 py-3 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask me anything..."
                    className="flex-1 resize-none border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 max-h-20"
                    rows={1}
                    disabled={loading}
                    data-testid="ai-assistant-input"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={loading || !input.trim()}
                    className="p-2.5 rounded-xl text-white transition-colors disabled:opacity-50"
                    style={{ backgroundColor: '#00A699' }}
                    data-testid="ai-assistant-send"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default EdgeAIAssistant;
